import { useCurrentUser } from "@qualio/ui-components";
import { useQuery } from "react-query";

import { qriApiClient } from "../api/qri";

type UseResolveQriNames = {
  /** Resolved name per QRI. Unresolvable QRIs are absent. */
  names: Record<string, string>;
  isLoading: boolean;
};

/**
 * Resolves a set of QRIs to their resource names via the QRI service.
 *
 * The query key is derived from the QRIs themselves rather than the array
 * identity, so callers do not have to memoise what they pass in. Passing an
 * empty array disables the request.
 */
export const useResolveQriNames = (
  qris: readonly string[],
): UseResolveQriNames => {
  const { companyId } = useCurrentUser();
  const uniqueQris = Array.from(new Set(qris)).sort();

  const { data, isLoading } = useQuery(
    ["qri-details", companyId, uniqueQris.join(",")],
    () => qriApiClient.resolveQriNames(uniqueQris),
    {
      refetchInterval: (data) =>
        data && Object.keys(data).length === uniqueQris.length ? false : 3000,
    },
  );

  return { names: data ?? {}, isLoading };
};
